import { LoaderFunctionArgs, useLoaderData, useNavigate, Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import moment from "moment/moment";
import toast from "react-hot-toast";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_DEVICE_DETAIL } from "./queries";
import { UPDATE_DEVICE_BY_PK } from "./mutations";
import { Device } from "../../types/device";
import { usePermissions } from "../auth/api";
import { SecondaryButton } from "../../components/Button";

const client = createApolloClient();

const DEFAULT_LIMIT = 10;

interface OrderRow {
  id: number;
  reference_id: string;
  subtotal: number | null;
  status: string | null;
  created_at: string;
  transaction_at: string | null;
  event: { id: number; name: string } | null;
}

interface LoaderResult {
  device: Device | null;
  orders: OrderRow[];
}

export const deviceDetailLoader = async ({ params }: LoaderFunctionArgs): Promise<LoaderResult> => {
  const id = Number(params.id);
  if (!Number.isFinite(id) || id <= 0) return { device: null, orders: [] };

  const { data } = await client.query({
    query: GET_DEVICE_DETAIL,
    variables: { id, limit: DEFAULT_LIMIT },
    fetchPolicy: "network-only",
  });

  return {
    device: (data?.devices_by_pk ?? null) as Device | null,
    orders: (data?.orders ?? []) as OrderRow[],
  };
};

const usd = (cents: number | null | undefined) =>
  cents == null ? "—" : `$${(cents / 100).toFixed(2)}`;

const verdictFor = (heartbeatAt: string | null | undefined) => {
  if (!heartbeatAt) return { label: "Unknown", pillBg: "bg-gray-100", pillText: "text-gray-800" };
  const minutes = moment().diff(moment(heartbeatAt), "minutes", true);
  if (minutes < 5) return { label: "Online", pillBg: "bg-green-100", pillText: "text-green-800" };
  if (minutes < 60) return { label: "Idle", pillBg: "bg-gray-100", pillText: "text-gray-800" };
  if (minutes < 60 * 24) return { label: "Stale", pillBg: "bg-yellow-100", pillText: "text-yellow-800" };
  return { label: "Offline", pillBg: "bg-red-100", pillText: "text-red-800" };
};

// Editable form value type — only the device columns the user can edit on
// this page. Numeric/cost fields are kept as strings to allow trailing
// decimals while typing; we coerce on save.
type EditableField =
  | "mac_address"
  | "license_plate"
  | "manufacturer"
  | "model_number"
  | "internal_part_number"
  | "android_device_id"
  | "item_type"
  | "item_status"
  | "deployment_status"
  | "current_possession"
  | "deployed_event_name"
  | "deployed_at"
  | "warehouse_desc"
  | "item_summary_desc"
  | "item_description"
  | "card_reader_serial"
  | "card_reader_pin"
  | "pcb_serial"
  | "sim_imei"
  | "replacement_cost_dollars"
  | "weight_lbs"
  | "length_in"
  | "width_in"
  | "height_in";

type FormState = Record<EditableField, string>;

const buildInitialForm = (d: Device): FormState => ({
  mac_address:          d.mac_address ?? "",
  license_plate:        d.license_plate ?? "",
  manufacturer:         d.manufacturer ?? "",
  model_number:         d.model_number ?? "",
  internal_part_number: d.internal_part_number ?? "",
  android_device_id:    d.android_device_id ?? "",
  item_type:            d.item_type ?? "",
  item_status:          d.item_status ?? "",
  deployment_status:    d.deployment_status ?? "",
  current_possession:   d.current_possession ?? "",
  deployed_event_name:  d.deployed_event_name ?? "",
  deployed_at:          d.deployed_at ? moment(d.deployed_at).format("YYYY-MM-DD") : "",
  warehouse_desc:       d.warehouse_desc ?? "",
  item_summary_desc:    d.item_summary_desc ?? "",
  item_description:     d.item_description ?? "",
  card_reader_serial:   d.card_reader_serial ?? "",
  card_reader_pin:      d.card_reader_pin ?? "",
  pcb_serial:           d.pcb_serial ?? "",
  sim_imei:             d.sim_imei ?? "",
  replacement_cost_dollars:
    d.replacement_cost_cents != null ? (d.replacement_cost_cents / 100).toFixed(2) : "",
  weight_lbs:           d.weight_lbs != null ? String(d.weight_lbs) : "",
  length_in:            d.length_in != null ? String(d.length_in) : "",
  width_in:             d.width_in != null ? String(d.width_in) : "",
  height_in:            d.height_in != null ? String(d.height_in) : "",
});

const Field = ({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "number" | "date";
  placeholder?: string;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-500">{label}</label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      step={type === "number" ? "any" : undefined}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
    />
  </div>
);

const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="bg-white shadow rounded-lg mb-6">
    <div className="px-6 py-4 border-b border-gray-200">
      <h2 className="text-lg font-medium text-gray-900">{title}</h2>
    </div>
    <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
      {children}
    </div>
  </div>
);

export const DeviceDetail = () => {
  const { device, orders } = useLoaderData() as LoaderResult;
  const { editCreatePermission } = usePermissions("devices");
  const navigate = useNavigate();

  const [current, setCurrent] = useState<Device | null>(device);
  const [form, setForm] = useState<FormState | null>(device ? buildInitialForm(device) : null);
  const [saving, setSaving] = useState(false);

  // Keep a reference snapshot to compare against for dirty-state detection.
  const [pristine, setPristine] = useState<FormState | null>(device ? buildInitialForm(device) : null);

  useEffect(() => {
    if (device) {
      const initial = buildInitialForm(device);
      setCurrent(device);
      setForm(initial);
      setPristine(initial);
    }
  }, [device]);

  const verdict = useMemo(() => verdictFor(current?.updated_at), [current?.updated_at]);
  const latestEvent = orders[0]?.event ?? null;
  const latestOrder = orders[0] ?? null;
  const lateDelta =
    latestOrder?.transaction_at && latestOrder?.created_at
      ? moment(latestOrder.created_at).diff(moment(latestOrder.transaction_at), "minutes")
      : null;
  const lateNote =
    lateDelta != null && lateDelta > 60
      ? `Most recent order arrived ${moment.duration(lateDelta, "minutes").humanize()} after the transaction time.`
      : null;

  const setField = (k: EditableField) => (v: string) => {
    setForm((prev) => (prev ? { ...prev, [k]: v } : prev));
  };

  const isDirty = useMemo(() => {
    if (!form || !pristine) return false;
    return (Object.keys(form) as EditableField[]).some((k) => form[k] !== pristine[k]);
  }, [form, pristine]);

  const handleSave = async () => {
    if (!current || !form) return;
    setSaving(true);
    try {
      // Coerce strings → DB types and only send fields that changed.
      const toNumOrNull = (s: string) => (s.trim() === "" ? null : Number(s));
      const toStringOrNull = (s: string) => (s.trim() === "" ? null : s);
      const toDateOrNull = (s: string) =>
        s.trim() === "" ? null : moment(s).startOf("day").toISOString();
      const toCentsOrNull = (s: string) =>
        s.trim() === "" ? null : Math.round(Number(s) * 100);

      const next: Record<string, any> = {
        mac_address:           toStringOrNull(form.mac_address),
        license_plate:         toStringOrNull(form.license_plate),
        manufacturer:          toStringOrNull(form.manufacturer),
        model_number:          toStringOrNull(form.model_number),
        internal_part_number:  toStringOrNull(form.internal_part_number),
        android_device_id:     toStringOrNull(form.android_device_id),
        item_type:             toStringOrNull(form.item_type),
        item_status:           toStringOrNull(form.item_status),
        deployment_status:     toStringOrNull(form.deployment_status),
        current_possession:    toStringOrNull(form.current_possession),
        deployed_event_name:   toStringOrNull(form.deployed_event_name),
        deployed_at:           toDateOrNull(form.deployed_at),
        warehouse_desc:        toStringOrNull(form.warehouse_desc),
        item_summary_desc:     toStringOrNull(form.item_summary_desc),
        item_description:      toStringOrNull(form.item_description),
        card_reader_serial:    toStringOrNull(form.card_reader_serial),
        card_reader_pin:       toStringOrNull(form.card_reader_pin),
        pcb_serial:            toStringOrNull(form.pcb_serial),
        sim_imei:              toStringOrNull(form.sim_imei),
        replacement_cost_cents: toCentsOrNull(form.replacement_cost_dollars),
        weight_lbs:            toNumOrNull(form.weight_lbs),
        length_in:             toNumOrNull(form.length_in),
        width_in:              toNumOrNull(form.width_in),
        height_in:             toNumOrNull(form.height_in),
      };

      await client.mutate({
        mutation: UPDATE_DEVICE_BY_PK,
        variables: { id: current.id, input: next },
      });

      const merged: Device = { ...current, ...next } as Device;
      setCurrent(merged);
      setPristine(form);
      toast.success("Device updated");
    } catch (e: any) {
      toast.error("Failed to update device: " + (e?.message ?? ""));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (pristine) setForm(pristine);
  };

  if (!current || !form) {
    return (
      <div className="max-w-5xl mx-auto py-6 px-4">
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          Device not found.
        </div>
      </div>
    );
  }

  const readOnly = !editCreatePermission;

  return (
    <div className="max-w-5xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {current.mac_address || `Device #${current.id}`}
          </h1>
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${verdict.pillBg} ${verdict.pillText}`}>
            {verdict.label}
          </span>
          {current.license_plate && (
            <span className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-gray-100 text-gray-800">
              #{current.license_plate}
            </span>
          )}
        </div>
        <SecondaryButton onClick={() => navigate("/devices/list")}>
          Back to List
        </SecondaryButton>
      </div>

      {/* Quick stats — read only */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">At a glance</h2>
        </div>
        <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Last seen</p>
            <p className="text-sm text-gray-900">{current.updated_at ? moment(current.updated_at).fromNow() : "—"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Last order</p>
            <p className="text-sm text-gray-900">{latestOrder?.created_at ? moment(latestOrder.created_at).fromNow() : "—"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Last order event</p>
            <p className="text-sm text-gray-900">{latestEvent?.name ?? "—"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Created</p>
            <p className="text-sm text-gray-900">{current.created_at ? moment(current.created_at).format("MMM DD, YYYY") : "—"}</p>
          </div>
        </div>
        {lateNote && (
          <div className="px-6 pb-4 text-sm text-yellow-700">{lateNote}</div>
        )}
      </div>

      {/* Editable cards */}
      <fieldset disabled={readOnly || saving} className="contents">
        <Card title="Identity">
          <Field label="MAC Address"          value={form.mac_address}          onChange={setField("mac_address")} />
          <Field label="License Plate"        value={form.license_plate}        onChange={setField("license_plate")} />
          <Field label="Manufacturer"         value={form.manufacturer}         onChange={setField("manufacturer")} />
          <Field label="Model Number"         value={form.model_number}         onChange={setField("model_number")} />
          <Field label="Internal Part Number" value={form.internal_part_number} onChange={setField("internal_part_number")} />
          <Field label="Android Device ID"    value={form.android_device_id}    onChange={setField("android_device_id")} />
          <Field label="Item Type"            value={form.item_type}            onChange={setField("item_type")} />
          <Field label="Item Status"          value={form.item_status}          onChange={setField("item_status")} placeholder="Active / Inactive" />
        </Card>

        <Card title="Deployment">
          <Field label="Deployment Status"    value={form.deployment_status}    onChange={setField("deployment_status")} placeholder="OUT / IN" />
          <Field label="Current Possession"   value={form.current_possession}   onChange={setField("current_possession")} />
          <Field label="Deployed Event"       value={form.deployed_event_name}  onChange={setField("deployed_event_name")} />
          <Field label="Deployed At"          value={form.deployed_at}          onChange={setField("deployed_at")} type="date" />
          <Field label="Warehouse Description"value={form.warehouse_desc}       onChange={setField("warehouse_desc")} />
          <Field label="Item Summary"         value={form.item_summary_desc}    onChange={setField("item_summary_desc")} />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-500">Item Description</label>
            <textarea
              rows={2}
              value={form.item_description}
              onChange={(e) => setField("item_description")(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
        </Card>

        <Card title="Peripherals">
          <Field label="Card Reader Serial"   value={form.card_reader_serial}   onChange={setField("card_reader_serial")} />
          <Field label="Card Reader PIN"      value={form.card_reader_pin}      onChange={setField("card_reader_pin")} />
          <Field label="PCB Serial"           value={form.pcb_serial}           onChange={setField("pcb_serial")} />
          <Field label="SIM IMEI"             value={form.sim_imei}             onChange={setField("sim_imei")} />
        </Card>

        <Card title="Physical">
          <Field label="Replacement Cost (USD)" value={form.replacement_cost_dollars} onChange={setField("replacement_cost_dollars")} type="number" />
          <Field label="Weight (lbs)"          value={form.weight_lbs}          onChange={setField("weight_lbs")} type="number" />
          <Field label="Length (in)"           value={form.length_in}           onChange={setField("length_in")} type="number" />
          <Field label="Width (in)"            value={form.width_in}            onChange={setField("width_in")} type="number" />
          <Field label="Height (in)"           value={form.height_in}           onChange={setField("height_in")} type="number" />
        </Card>
      </fieldset>

      {/* Recent transactions — read only */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Recent transactions</h2>
          <span className="text-xs text-gray-400 tabular-nums">{orders.length} of last {DEFAULT_LIMIT}</span>
        </div>
        {orders.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">No transactions found for this device.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">When</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Reference</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((o) => {
                const arrivedLate =
                  o.transaction_at && moment(o.created_at).diff(moment(o.transaction_at), "minutes") > 60;
                return (
                  <tr key={`${o.id}-${o.reference_id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900">
                      <Link
                        to={`/orders/${encodeURIComponent(o.reference_id)}/${o.id}`}
                        className="hover:text-primary-700"
                      >
                        {moment(o.created_at).format("MMM D, YYYY h:mm a")}
                      </Link>
                      {arrivedLate && (
                        <span className="ml-2 text-xs text-yellow-700">
                          (late by {moment.duration(moment(o.created_at).diff(moment(o.transaction_at!)), "ms").humanize()})
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">{o.event?.name ?? "—"}</td>
                    <td className="px-6 py-3 text-sm text-gray-600 capitalize">{o.status ?? "—"}</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right tabular-nums">{usd(o.subtotal)}</td>
                    <td className="px-6 py-3 text-xs text-gray-400 text-right tabular-nums truncate">{o.reference_id}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Save / Cancel Footer */}
      <div className="flex justify-end gap-3">
        <button
          onClick={handleCancel}
          type="button"
          disabled={!isDirty || saving}
          className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
          style={{ lineHeight: 2 }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          type="button"
          disabled={readOnly || saving || !isDirty}
          className="inline-flex justify-center items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
          style={{ lineHeight: 2 }}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};
